/**
 * Billing Dashboard — Revenue Operations Center
 * 
 * The executive view for revenue operations.
 * 
 * When leadership opens Billing they should immediately know:
 * - Is billing ready?
 * - What revenue is available?
 * - What is preventing submission?
 * - Which authorizations are at risk?
 * - Which claims need attention?
 * - What cash is outstanding?
 * 
 * No searching. No reports. Immediate operational awareness.
 */
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  DollarSign, Clock, AlertTriangle, CheckCircle, Download,
  FileText, Users, Calendar, ChevronRight, Loader2, RefreshCw,
  Ban, AlertCircle, FileCheck, ExternalLink, Search, 
  TrendingUp, ArrowRight, CreditCard, Receipt, Shield,
  Timer, Hourglass, CircleDollarSign, BadgeCheck, XCircle,
  ChevronDown, BarChart3, PieChart
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import api from "../../lib/api";

// Revenue Readiness Ring - Large executive component
const RevenueReadinessRing = ({ percentage, readyAmount, blockedAmount, estimatedMinutes }) => {
  const size = 160;
  const strokeWidth = 14;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;
  
  const getColor = () => {
    if (percentage >= 90) return "#22c55e"; // green
    if (percentage >= 70) return "#f59e0b"; // yellow
    return "#ef4444"; // red
  };
  
  return (
    <div className="flex flex-col items-center">
      <h3 className="text-sm font-medium text-muted-foreground mb-4">Revenue Readiness</h3>
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="transform -rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            className="text-secondary"
          />
          <motion.circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={getColor()}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            initial={{ strokeDashoffset: circumference }}
            animate={{ strokeDashoffset: offset }}
            transition={{ duration: 1, ease: "easeOut" }}
            style={{ strokeDasharray: circumference }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-4xl font-bold" style={{ color: getColor() }}>
            {percentage}%
          </span>
          <span className="text-xs text-muted-foreground">Ready</span>
        </div>
      </div>
      <div className="mt-4 text-center space-y-1">
        <div className="text-sm">
          <span className="text-green-600 font-semibold">${readyAmount.toLocaleString()}</span>
          <span className="text-muted-foreground"> Ready</span>
        </div>
        {blockedAmount > 0 && (
          <div className="text-sm">
            <span className="text-red-600 font-semibold">${blockedAmount.toLocaleString()}</span>
            <span className="text-muted-foreground"> Blocked</span>
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-2">
          Est. submission time: <strong>{estimatedMinutes} min</strong>
        </p>
      </div>
    </div>
  );
};

// Stat card component
const StatCard = ({ label, value, subValue, icon: Icon, trend, color, onClick }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className={`surface p-4 ${onClick ? "cursor-pointer hover:shadow-md transition-shadow" : ""}`}
    onClick={onClick}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
        <p className={`text-2xl font-bold mt-1 ${color || ""}`}>{value}</p>
        {subValue && <p className="text-xs text-muted-foreground mt-0.5">{subValue}</p>}
      </div>
      <div className={`p-2 rounded-lg ${color ? "bg-opacity-10" : "bg-secondary"}`} style={color ? {backgroundColor: `${color}15`} : {}}>
        <Icon className={`w-5 h-5 ${color || "text-muted-foreground"}`} />
      </div>
    </div>
    {trend && (
      <div className="mt-2 flex items-center gap-1 text-xs">
        <TrendingUp className="w-3 h-3 text-green-600" />
        <span className="text-green-600">{trend}</span>
      </div>
    )}
  </motion.div>
);

// Blocking issue card for billing
const BillingBlockCard = ({ issue, onResolve }) => (
  <div className="p-3 rounded-lg border border-red-200 bg-red-50">
    <div className="flex items-start gap-3">
      <div className="p-1.5 rounded bg-red-100">
        <AlertTriangle className="w-4 h-4 text-red-600" />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-red-800">{issue.title}</h4>
        <p className="text-xs text-red-600 mt-0.5">{issue.description}</p>
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <Badge variant="destructive" className="text-[10px]">
            ${issue.revenue_impact?.toLocaleString() || 0} at risk
          </Badge>
          {issue.client_name && (
            <span className="text-xs text-red-600">Client: {issue.client_name}</span>
          )}
          {issue.caregiver_name && (
            <span className="text-xs text-red-600">Caregiver: {issue.caregiver_name}</span>
          )}
        </div>
      </div>
      <Button 
        variant="outline" 
        size="sm" 
        className="shrink-0 border-red-300 text-red-700 hover:bg-red-100"
        onClick={() => onResolve(issue)}
      >
        Resolve
        <ChevronRight className="w-3 h-3 ml-1" />
      </Button>
    </div>
  </div>
);

// Authorization warning card
const AuthorizationCard = ({ auth, onAction }) => {
  const usedPercent = auth.approved_hours > 0 
    ? Math.round((auth.used_hours / auth.approved_hours) * 100) 
    : 0;
  const remaining = auth.approved_hours - auth.used_hours;
  
  const getWarningLevel = () => {
    if (auth.days_until_expiry <= 7 || usedPercent >= 95) return "critical";
    if (auth.days_until_expiry <= 14 || usedPercent >= 85) return "warning";
    return "normal";
  };
  
  const level = getWarningLevel();
  const colors = {
    critical: "border-red-200 bg-red-50",
    warning: "border-yellow-200 bg-yellow-50",
    normal: "border-border bg-card"
  };
  
  return (
    <div className={`p-3 rounded-lg border ${colors[level]}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium truncate">{auth.client_name}</h4>
          <p className="text-xs text-muted-foreground">{auth.funding_source}</p>
          
          {/* Hours usage bar */}
          <div className="mt-2">
            <div className="flex justify-between text-xs mb-1">
              <span>{auth.used_hours}h used</span>
              <span>{remaining}h remaining</span>
            </div>
            <div className="h-2 bg-secondary rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all ${
                  usedPercent >= 95 ? "bg-red-500" : usedPercent >= 85 ? "bg-yellow-500" : "bg-green-500"
                }`}
                style={{ width: `${Math.min(usedPercent, 100)}%` }}
              />
            </div>
          </div>
          
          <div className="flex items-center gap-2 mt-2">
            <Badge variant={level === "critical" ? "destructive" : level === "warning" ? "secondary" : "outline"} className="text-[10px]">
              {usedPercent}% used
            </Badge>
            <span className="text-xs text-muted-foreground">
              Expires in {auth.days_until_expiry} days
            </span>
          </div>
        </div>
        
        {level !== "normal" && (
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => onAction(auth, "extend")}
          >
            Extend
          </Button>
        )}
      </div>
    </div>
  );
};

// Claims pipeline visualization
const ClaimsPipeline = ({ stages }) => {
  const stageLabels = {
    draft: "Draft",
    ready: "Ready",
    approved: "Approved", 
    submitted: "Submitted",
    accepted: "Accepted",
    paid: "Paid"
  };
  
  const stageColors = {
    draft: "bg-gray-100 text-gray-700",
    ready: "bg-blue-100 text-blue-700",
    approved: "bg-indigo-100 text-indigo-700",
    submitted: "bg-purple-100 text-purple-700",
    accepted: "bg-green-100 text-green-700",
    paid: "bg-emerald-100 text-emerald-700"
  };
  
  return (
    <div className="flex items-center gap-1 overflow-x-auto pb-2">
      {Object.entries(stageLabels).map(([key, label], index) => (
        <React.Fragment key={key}>
          <div className={`flex flex-col items-center px-3 py-2 rounded-lg ${stageColors[key]} min-w-[80px]`}>
            <span className="text-lg font-bold">{stages[key] || 0}</span>
            <span className="text-[10px] uppercase tracking-wide">{label}</span>
            {stages[`${key}_amount`] && (
              <span className="text-xs mt-0.5">${stages[`${key}_amount`]?.toLocaleString()}</span>
            )}
          </div>
          {index < Object.keys(stageLabels).length - 1 && (
            <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// Revenue/Claim card
const ClaimCard = ({ claim, onSubmit, onView }) => {
  const checks = [
    { key: "documents", label: "Documents", ok: claim.documents_complete },
    { key: "evv", label: "EVV", ok: claim.evv_complete },
    { key: "auth", label: "Authorization", ok: claim.authorization_valid },
    { key: "notes", label: "Care Notes", ok: claim.care_notes_complete }
  ];
  
  const allOk = checks.every(c => c.ok);
  
  return (
    <div className="p-4 rounded-lg border bg-card hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-medium truncate">{claim.client_name}</h4>
            <Badge variant={claim.status === "ready" ? "default" : "secondary"} className="text-[10px]">
              {claim.status}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground">{claim.funding_source}</p>
        </div>
        <div className="text-right">
          <p className="text-lg font-bold text-green-600">${claim.amount?.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">{claim.service_hours}h</p>
        </div>
      </div>
      
      {/* Compliance checks */}
      <div className="flex items-center gap-3 mt-3">
        {checks.map(check => (
          <div key={check.key} className="flex items-center gap-1">
            {check.ok ? (
              <CheckCircle className="w-3.5 h-3.5 text-green-600" />
            ) : (
              <XCircle className="w-3.5 h-3.5 text-red-500" />
            )}
            <span className={`text-xs ${check.ok ? "text-green-700" : "text-red-600"}`}>
              {check.label}
            </span>
          </div>
        ))}
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-2 mt-3 pt-3 border-t">
        <Button variant="ghost" size="sm" onClick={() => onView(claim)}>
          View Details
        </Button>
        {claim.status === "ready" && allOk && (
          <Button size="sm" onClick={() => onSubmit(claim)}>
            Submit Claim
            <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
};

// Export button component
const ExportButton = ({ format, label, icon: Icon, disabled, onExport }) => (
  <Button
    variant="outline"
    size="sm"
    disabled={disabled}
    onClick={() => onExport(format)}
    className="flex items-center gap-2"
  >
    <Icon className="w-4 h-4" />
    {label}
    <Download className="w-3 h-3 ml-1" />
  </Button>
);

export default function BillingDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [billingData, setBillingData] = useState(null);
  const [claims, setClaims] = useState([]);
  const [authorizations, setAuthorizations] = useState([]);
  const [blockingIssues, setBlockingIssues] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("current");
  const [claimFilter, setClaimFilter] = useState("all");
  const [refreshing, setRefreshing] = useState(false);

  const loadBillingData = useCallback(async () => {
    try {
      // Load billing summary
      const summaryRes = await api.get("/billing/summary");
      setBillingData(summaryRes.data);
      
      // Load claims
      const claimsRes = await api.get("/billing/claims");
      setClaims(claimsRes.data.claims || []);
      
      // Load authorizations at risk
      const authRes = await api.get("/billing/authorizations/at-risk");
      setAuthorizations(authRes.data.authorizations || []);
      
      // Extract blocking issues
      const issues = [];
      if (summaryRes.data.blocked_claims > 0) {
        // Generate issues based on blocking reasons
        if (summaryRes.data.missing_signatures > 0) {
          issues.push({
            id: "missing_signatures",
            title: "Missing Signatures",
            description: `${summaryRes.data.missing_signatures} claim(s) are missing required signatures`,
            revenue_impact: summaryRes.data.blocked_revenue * 0.3,
            resolve_url: "/review?filter=missing_signature"
          });
        }
        if (summaryRes.data.expired_authorizations > 0) {
          issues.push({
            id: "expired_auth",
            title: "Authorization Expired",
            description: `${summaryRes.data.expired_authorizations} claim(s) have expired authorizations`,
            revenue_impact: summaryRes.data.blocked_revenue * 0.4,
            resolve_url: "/clients?filter=auth_expired"
          });
        }
        if (summaryRes.data.missing_care_notes > 0) {
          issues.push({
            id: "missing_notes",
            title: "Care Notes Missing",
            description: `${summaryRes.data.missing_care_notes} visit(s) are missing required care notes`,
            revenue_impact: summaryRes.data.blocked_revenue * 0.2,
            resolve_url: "/review?filter=missing_notes"
          });
        }
        if (summaryRes.data.evv_failures > 0) {
          issues.push({
            id: "evv_failures",
            title: "EVV Verification Failed",
            description: `${summaryRes.data.evv_failures} visit(s) have EVV compliance issues`,
            revenue_impact: summaryRes.data.blocked_revenue * 0.1,
            resolve_url: "/exceptions?type=evv"
          });
        }
      }
      setBlockingIssues(issues);
      
    } catch (err) {
      console.error("Failed to load billing data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBillingData();
  }, [loadBillingData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadBillingData();
    setRefreshing(false);
  };

  const handleResolveIssue = (issue) => {
    navigate(issue.resolve_url);
  };

  const handleAuthAction = (auth, action) => {
    if (action === "extend") {
      navigate(`/clients/${auth.client_id}?tab=authorizations`);
    }
  };

  const handleSubmitClaim = async (claim) => {
    // TODO: Implement claim submission
    alert(`Submitting claim for ${claim.client_name}...`);
  };

  const handleViewClaim = (claim) => {
    navigate(`/clients/${claim.client_id}?tab=billing`);
  };

  const handleExport = async (format) => {
    // TODO: Implement actual export
    alert(`Exporting billing in ${format} format...`);
  };

  // Filter claims
  const filteredClaims = claims.filter(claim => 
    claimFilter === "all" || claim.status === claimFilter
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const readinessPercentage = billingData?.readiness_percentage || 0;
  const revenueReady = billingData?.revenue_ready || 0;
  const revenueBlocked = billingData?.revenue_blocked || 0;
  const totalRevenue = billingData?.total_revenue || 0;
  const claimsReady = billingData?.claims_ready || 0;
  const claimsPending = billingData?.claims_pending || 0;
  const claimsBlocked = billingData?.blocked_claims || 0;
  const avgDaysOutstanding = billingData?.avg_days_outstanding || 0;
  const collectionsThisMonth = billingData?.collections_this_month || 0;

  const pipelineStages = {
    draft: billingData?.pipeline?.draft || 0,
    ready: billingData?.pipeline?.ready || claimsReady,
    approved: billingData?.pipeline?.approved || 0,
    submitted: billingData?.pipeline?.submitted || 0,
    accepted: billingData?.pipeline?.accepted || 0,
    paid: billingData?.pipeline?.paid || 0
  };

  return (
    <div className="px-4 md:px-8 py-6 md:py-8 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="heading-display flex items-center gap-3">
            <CircleDollarSign className="w-8 h-8 text-green-600" />
            Revenue Operations
          </h1>
          <p className="text-muted-foreground mt-1">
            Billing control center — immediate revenue awareness
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="current">Current Period</SelectItem>
              <SelectItem value="previous">Previous Period</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Executive Summary Row */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Revenue Readiness Ring */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="lg:col-span-1 surface p-6 flex items-center justify-center"
        >
          <RevenueReadinessRing
            percentage={readinessPercentage}
            readyAmount={revenueReady}
            blockedAmount={revenueBlocked}
            estimatedMinutes={Math.max(5, blockingIssues.length * 6)}
          />
        </motion.div>

        {/* Stats Grid */}
        <div className="lg:col-span-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="Revenue Ready"
            value={`$${revenueReady.toLocaleString()}`}
            subValue={`${claimsReady} claims ready`}
            icon={DollarSign}
            color="text-green-600"
          />
          <StatCard
            label="Revenue Blocked"
            value={`$${revenueBlocked.toLocaleString()}`}
            subValue={`${claimsBlocked} claims blocked`}
            icon={Ban}
            color="text-red-600"
            onClick={() => navigate("/review")}
          />
          <StatCard
            label="Outstanding"
            value={`$${(billingData?.revenue_outstanding || 0).toLocaleString()}`}
            subValue={`Avg ${avgDaysOutstanding} days`}
            icon={Hourglass}
          />
          <StatCard
            label="Collections"
            value={`$${collectionsThisMonth.toLocaleString()}`}
            subValue="This month"
            icon={CreditCard}
            trend={billingData?.collections_trend || null}
          />
        </div>
      </div>

      {/* Claims Pipeline */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="surface p-5"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Claims Pipeline
          </h2>
          <span className="text-sm text-muted-foreground">
            Total: ${totalRevenue.toLocaleString()}
          </span>
        </div>
        <ClaimsPipeline stages={pipelineStages} />
      </motion.div>

      {/* Blocking Issues */}
      {blockingIssues.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-3"
        >
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Ban className="w-5 h-5 text-red-600" />
              Blocking Issues ({blockingIssues.length})
            </h2>
            <span className="text-sm text-red-600 font-medium">
              ${revenueBlocked.toLocaleString()} at risk
            </span>
          </div>
          <div className="grid gap-3">
            {blockingIssues.map(issue => (
              <BillingBlockCard 
                key={issue.id} 
                issue={issue} 
                onResolve={handleResolveIssue}
              />
            ))}
          </div>
        </motion.div>
      )}

      {/* Authorization Center */}
      {authorizations.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-3"
        >
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Shield className="w-5 h-5 text-yellow-600" />
              Authorization Warnings ({authorizations.length})
            </h2>
            <Button variant="ghost" size="sm" onClick={() => navigate("/clients?tab=authorizations")}>
              View All
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {authorizations.slice(0, 6).map(auth => (
              <AuthorizationCard
                key={auth.id}
                auth={auth}
                onAction={handleAuthAction}
              />
            ))}
          </div>
        </motion.div>
      )}

      {/* Claims / Revenue Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-4"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Receipt className="w-5 h-5" />
            Claims ({filteredClaims.length})
          </h2>
          <div className="flex items-center gap-2">
            <Select value={claimFilter} onValueChange={setClaimFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Claims</SelectItem>
                <SelectItem value="ready">Ready</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="blocked">Blocked</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {filteredClaims.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredClaims.slice(0, 9).map(claim => (
              <ClaimCard
                key={claim.id}
                claim={claim}
                onSubmit={handleSubmitClaim}
                onView={handleViewClaim}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground surface rounded-lg">
            <Receipt className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p>No claims found for the selected filter</p>
          </div>
        )}
        
        {filteredClaims.length > 9 && (
          <div className="text-center">
            <Button variant="outline">
              View All {filteredClaims.length} Claims
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}
      </motion.div>

      {/* Export Center */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="surface p-5"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Download className="w-5 h-5" />
            Export Billing
          </h2>
          <Badge variant={readinessPercentage >= 100 ? "default" : "secondary"}>
            {readinessPercentage >= 100 ? "Ready to Export" : `${claimsReady} claims ready`}
          </Badge>
        </div>
        
        <div className="flex flex-wrap gap-3">
          <ExportButton
            format="csv"
            label="CSV"
            icon={FileText}
            disabled={claimsReady === 0}
            onExport={handleExport}
          />
          <ExportButton
            format="837p"
            label="837P (Electronic)"
            icon={ExternalLink}
            disabled={claimsReady === 0}
            onExport={handleExport}
          />
          <ExportButton
            format="hcfa"
            label="HCFA-1500"
            icon={FileText}
            disabled={claimsReady === 0}
            onExport={handleExport}
          />
          <ExportButton
            format="quickbooks"
            label="QuickBooks"
            icon={ExternalLink}
            disabled={claimsReady === 0}
            onExport={handleExport}
          />
        </div>
      </motion.div>

      {/* Collections Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="surface p-5"
      >
        <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
          <CreditCard className="w-5 h-5" />
          Collections & Payments
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 rounded-lg bg-green-50 border border-green-200">
            <p className="text-xs text-green-700 uppercase tracking-wide">Collected This Month</p>
            <p className="text-2xl font-bold text-green-700">${collectionsThisMonth.toLocaleString()}</p>
          </div>
          <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
            <p className="text-xs text-yellow-700 uppercase tracking-wide">Outstanding</p>
            <p className="text-2xl font-bold text-yellow-700">${(billingData?.revenue_outstanding || 0).toLocaleString()}</p>
          </div>
          <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
            <p className="text-xs text-blue-700 uppercase tracking-wide">Avg. Payment Time</p>
            <p className="text-2xl font-bold text-blue-700">{avgDaysOutstanding} days</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
